import { Link, useLocation } from 'react-router';
import { api } from '../../shared/api/client';
import type { LiveSettings, LiveState } from '../../shared/api/types';
import { Card, EmptyState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { LiveBadge, LivePlayer, useLiveState } from './live';
import { LiveControl } from './LiveSettingsCard';

type MemberLive = LiveState & { churchName: string; slug: string };

export function useMemberLive(): MemberLive | null {
  const me = useMe();
  const path = me.activeChurchId && me.can('church.read') ? `/multichurch/churches/${me.activeChurchId}/live` : null;
  return useLiveState(path) as MemberLive | null;
}

/** Mi iglesia → «Iglesia en vivo»: el directo (o los videos en bucle) dentro de la cuenta del miembro. */
export function LivePage() {
  const me = useMe();
  const live = useMemberLive();
  const churchId = me.activeChurchId;
  // Quien opera la transmisión (administración o Multimedia) también la prende y la apaga desde aquí.
  const control = useAsync(
    (signal) => (churchId && (me.can('church.manage') || me.can('presentation.operate')) ? api.get<LiveSettings>(`/multichurch/churches/${churchId}/live/settings`, signal).catch(() => null) : Promise.resolve(null)),
    [churchId],
  );
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (!live) return <div className="page"><Loading /></div>;
  return (
    <div className="page">
      <header className="page__header">
        <h1>Iglesia en vivo</h1>
        <p className="muted">
          {live.churchName} · <LiveBadge state={live} />
        </p>
      </header>
      {live.status === 'off' ? (
        <EmptyState title="Tu iglesia no está transmitiendo ahora.">
          <p>Cuando empiece la transmisión aparece aquí sola, y te avisamos arriba en cualquier pantalla.</p>
        </EmptyState>
      ) : (
        <LivePlayer state={live} title={live.churchName} />
      )}
      {live.status === 'live' && live.url ? (
        <p className="small">
          <a href={live.url} target="_blank" rel="noopener noreferrer">
            Abrir en {live.kind === 'youtube' ? 'YouTube' : 'Facebook'}
          </a>{' '}
          (para comentar o compartir)
        </p>
      ) : null}
      {control.status === 'ready' && control.data ? (
        <Card title="Transmisión">
          <LiveControl churchId={churchId} settings={control.data} onChanged={control.reload} />
          {control.data.canConfigure ? (
            <p className="small muted">
              El canal de YouTube, la página de Facebook y los videos en bucle se configuran en <Link to="/app/configuracion-iglesia#en-vivo">Configuración de la iglesia</Link>.
            </p>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

/** Aviso arriba de cualquier pantalla de la cuenta mientras la iglesia está en vivo. */
export function LiveNowBanner() {
  const live = useMemberLive();
  const { pathname } = useLocation();
  // En Inicio el directo ya se ve en su tarjeta.
  if (!live || live.status !== 'live' || pathname === '/app/en-vivo' || pathname === '/app' || pathname === '/app/') return null;
  return (
    <Link to="/app/en-vivo" className="live-banner">
      <span className="wall-live">● En vivo</span>
      <span>
        {live.churchName} está transmitiendo. <strong>Ver ahora</strong>
      </span>
    </Link>
  );
}

/** Inicio: el directo de la iglesia, solo mientras está en vivo; sin transmisión no aparece nada. */
export function HomeLiveCard() {
  const live = useMemberLive();
  if (!live || live.status !== 'live') return null;
  return (
    <section className="card home-live" aria-labelledby="home-live-title">
      <div className="card__header">
        <h2 id="home-live-title" className="card__title">
          <span className="wall-live">● En vivo</span> ahora
        </h2>
      </div>
      <LivePlayer state={live} title={live.churchName} />
      <div className="actions actions--wrap">
        <Link className="btn btn--primary" to="/app/en-vivo">
          Ver en pantalla completa
        </Link>
        {live.url ? (
          <a className="btn btn--ghost" href={live.url} target="_blank" rel="noopener noreferrer">
            Abrir en {live.kind === 'youtube' ? 'YouTube' : 'Facebook'}
          </a>
        ) : null}
      </div>
    </section>
  );
}
